import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useData } from "@/context/DataContext";
import { toast } from "sonner";

export default function EventEditModal({ event, open, onOpenChange }) {
  const { categories, updateEvent } = useData();
  const [form, setForm] = useState(event || {});

  useEffect(() => { setForm(event || {}); }, [event]);

  if (!event) return null;

  const save = async () => {
    try {
      await updateEvent(event.id, {
        title: form.title,
        date: form.date,
        start_time: form.start_time,
        end_time: form.end_time,
        kind: form.kind,
        category_id: form.category_id || null,
        location: form.location || null,
        notes: form.notes || null,
      });
      toast.success("Afspraak bijgewerkt");
      onOpenChange(false);
    } catch { toast.error("Bijwerken mislukt"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#121C2F] border-slate-800 text-slate-100 max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-slate-50 text-xl">Afspraak bewerken</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-400">Titel</Label>
            <Input value={form.title || ""} onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="mt-1.5 bg-slate-900 border-slate-800 text-slate-100" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-400">Datum</Label>
              <Input type="date" value={form.date || ""} onChange={(e) => setForm({ ...form, date: e.target.value })}
                className="mt-1.5 bg-slate-900 border-slate-800 text-slate-100" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-400">Categorie</Label>
              <Select value={form.category_id || ""} onValueChange={(v) => setForm({ ...form, category_id: v })}>
                <SelectTrigger className="mt-1.5 bg-slate-900 border-slate-800 text-slate-100"><SelectValue placeholder="Kies" /></SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-800 text-slate-100">
                  {categories.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      <span className="inline-flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full" style={{ background: c.color }} />{c.name}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-400">Start</Label>
              <Input type="time" value={form.start_time || ""} onChange={(e) => setForm({ ...form, start_time: e.target.value })}
                className="mt-1.5 bg-slate-900 border-slate-800 text-slate-100" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-400">Einde</Label>
              <Input type="time" value={form.end_time || ""} onChange={(e) => setForm({ ...form, end_time: e.target.value })}
                className="mt-1.5 bg-slate-900 border-slate-800 text-slate-100" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-400">Type</Label>
              <Select value={form.kind || "flexible"} onValueChange={(v) => setForm({ ...form, kind: v })}>
                <SelectTrigger className="mt-1.5 bg-slate-900 border-slate-800 text-slate-100"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-800 text-slate-100">
                  <SelectItem value="fixed">Vast</SelectItem>
                  <SelectItem value="flexible">Flexibel</SelectItem>
                  <SelectItem value="rest">Rust</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-400">Locatie</Label>
            <Input value={form.location || ""} onChange={(e) => setForm({ ...form, location: e.target.value })}
              className="mt-1.5 bg-slate-900 border-slate-800 text-slate-100" placeholder="Optioneel" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-400">Notities</Label>
            <Textarea value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="mt-1.5 bg-slate-900 border-slate-800 text-slate-100 min-h-[60px]" />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-2">
          <Button variant="ghost" className="text-slate-400 hover:text-slate-100 hover:bg-slate-800/60"
            onClick={() => onOpenChange(false)}>Annuleren</Button>
          <Button data-testid="event-edit-save" onClick={save} className="bg-blue-600 hover:bg-blue-500">Opslaan</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
